import { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth0Tokens } from '../../hooks/useAuth0Tokens';
import SyncButton from './SyncButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Chip,
} from '@mui/material';

const DATAGRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: 'background.subtle',
    borderBottom: 1,
    borderColor: 'divider',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .MuiDataGrid-cell': {
    color: 'text.primary',
    borderColor: 'divider',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-footerContainer': {
    bgcolor: 'background.subtle',
    borderTop: 1,
    borderColor: 'divider',
  },
  '& .MuiDataGrid-row:hover': {
    bgcolor: 'action.hover',
  },
};

const SPIN_SX = {
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
};

const JiraViewModal = ({ isOpen, onClose, orgs, setOrgs, onRefresh }) => {
  const { getTokens } = useAuth0Tokens();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (!isOpen) return null;

  const handleSync = async (orgName) => {
    // Note: Sync is only available for IDP integrations (Entra ID, Okta)
    // Jira integrations don't require sync as data is fetched on-demand
    alert('Sync is not available for Jira integrations. Jira data is fetched in real-time.');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const columns = [
    {
      field: 'index',
      headerName: '#',
      width: 60,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        try {
          return (params.api && typeof params.api.getRowIndexRelativeToVisibleRows === 'function')
            ? params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
            : (params.row.index || 0);
        } catch (e) {
          return params.row.index || 0;
        }
      }
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'org_name',
      headerName: 'Name',
      width: 120,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'date',
      headerName: 'Last Synced',
      width: 120,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Active' ? 'success' : params.value === 'Integrating...' ? 'warning' : 'error'}
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      field: 'comment',
      headerName: 'Comments',
      width: 250,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <SyncButton
          onSync={() => handleSync(params.row.org_name)}
          disabled={false}
        />
      )
    }
  ];

  const rows = orgs.map((org) => ({
    id: org.id,
    org_name: org.name,
    date: org.date,
    status: org.status,
    comment: org.comment
  }));

  const paginationModel = { page: 0, pageSize: 5 };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;

    try {
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
      const { apiToken } = await getTokens();

      // Get the selected integrations to delete
      const integrationsToDelete = orgs.filter(org => selectedRows.includes(org.id));

      // Delete each integration using GraphQL mutation
      const deletePromises = integrationsToDelete.map(async (integration) => {
        const mutation = `
          mutation DeleteIntegration($input: DeleteIntegrationInput!) {
            deleteIntegration(input: $input) {
              success
              message
              integrationId
              errors
            }
          }
        `;

        const variables = {
          input: {
            id: integration.id,
            integrationType: "jira",
            orgName: integration.name
          }
        };

        console.log('Deleting integration:', { id: integration.id, orgName: integration.name });

        const response = await fetch(graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          },
          body: JSON.stringify({ query: mutation, variables })
        });

        const responseData = await response.json();
        console.log('Delete response:', responseData);

        if (responseData.errors) {
          throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
        }

        const result = responseData.data?.deleteIntegration;
        if (!result || !result.success) {
          throw new Error(result?.message || result?.errors?.[0] || 'Failed to delete integration');
        }

        return result;
      });

      // Wait for all deletes to complete
      await Promise.all(deletePromises);

      // Update local state - remove deleted integrations
      const updatedOrgs = orgs.filter(org => !selectedRows.includes(org.id));
      setOrgs(updatedOrgs);
      setSelectedRows([]);

      console.log('Successfully deleted integrations');
    } catch (error) {
      console.error('Error deleting integrations:', error);
      alert(`Failed to delete integration: ${error.message}`);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Linked Instances</Typography>
          <IconButton onClick={handleRefresh} disabled={isRefreshing} size="small" color="primary">
            <RefreshIcon sx={isRefreshing ? SPIN_SX : {}} />
          </IconButton>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{ pagination: { paginationModel } }}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          slots={{
            toolbar: () => (
              selectedRows.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Button variant="contained" color="error" size="small" onClick={handleDelete}>
                    Delete selected ({selectedRows.length})
                  </Button>
                </Box>
              ) : null
            )
          }}
          sx={DATAGRID_SX}
          autoHeight
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 100}
        />
      </DialogContent>
    </Dialog>
  );
};

export default JiraViewModal;
